import { isNil } from 'ramda';
import {
  type FormBuilderFragment,
  type FormBuilder_EventFragment,
  type FormBuilder_Event_Field_Type,
  type FormBuilder_Event_Field_Type__Input,
  type FormBuilder_Event_Field__Input,
  type FormBuilder_Event__Input,
  type FormBuilder_NodeFragment,
  type FormBuilder_Node__Input,
  type FormBuilder_OptionList,
  type FormBuilder_OptionList__Input,
  type FormBuilder_ScreenNodeFragment,
  type FormBuilder_ScreenNode_Block__Input,
  type FormBuilder_StyleFragment,
  type FormBuilder_Style__Input,
  type FormBuilder__Input,
} from '~/graphql/types';
import getImageInput from '~/util/getImageInput';
import removeTypenames from '~/util/removeTypenames';

const serializeFormBuilderBlock = (
  block: FormBuilder_ScreenNodeFragment['blocks'][number],
): FormBuilder_ScreenNode_Block__Input => {
  if (block.__typename === 'FormData_UI_Image') {
    const imageInput = getImageInput(block.image);
    if (!imageInput) throw new Error('Invalid image type');
    return {
      FormData_UI_Image: {
        alt: removeTypenames(block.alt),
        image: imageInput,
        alignment: block.alignment,
        key: block.key,
        width: block.width ?? 800,
      },
    };
  }

  return { [block.__typename]: removeTypenames(block) };
};

const serializeFormBuilderNodes = (
  nodes: Array<FormBuilder_NodeFragment>,
): Array<FormBuilder_Node__Input> =>
  nodes.map((node): FormBuilder_Node__Input => {
    if (node.__typename === 'FormBuilder_ScreenNode') {
      return {
        FormBuilder_ScreenNode: {
          id: node.id,
          name: node.name,
          defaultNext: node.defaultNext
            ? removeTypenames(node.defaultNext)
            : undefined,
          blocks: node.blocks.map(
            (block): FormBuilder_ScreenNode_Block__Input =>
              serializeFormBuilderBlock(block),
          ),
        },
      };
    }

    return {
      FormBuilder_EventNode: {
        id: node.id,
        name: node.name,
        defaultNext: node.defaultNext
          ? removeTypenames(node.defaultNext)
          : undefined,
        formBuilderEventId: node.formBuilderEventId,
        mapping: removeTypenames(node.mapping),
      },
    };
  });

const serializeFormBuilderEventFieldType = (
  type: FormBuilder_Event_Field_Type,
): FormBuilder_Event_Field_Type__Input => {
  if (type.__typename === 'FormBuilder_Event_Field_Type_Primitive') {
    return {
      Primitive: {
        type: type.type,
      },
    };
  }

  return {
    Option: {
      id: type.id,
    },
  };
};

const serializeFormBuilderEvents = (
  events: Array<FormBuilder_EventFragment>,
): Array<FormBuilder_Event__Input> =>
  events.map(
    (event): FormBuilder_Event__Input => ({
      score: isNil(event.score) || isNaN(event.score) ? 0 : event.score,
      fields: event.fields.map(
        (field): FormBuilder_Event_Field__Input => ({
          key: field.key,
          name: field.name,
          metaField: field.metaField,
          type: serializeFormBuilderEventFieldType(field.type),
        }),
      ),
      id: event.id,
      name: event.name,
    }),
  );

const serializeFormBuilderStyle = (
  style: FormBuilder_StyleFragment,
): FormBuilder_Style__Input => ({
  availableLocale: style.availableLocale,
  layout: {
    FormBuilder_Style_PageLayout_Default: {
      backgroundColor: style.layout.backgroundColor,
      backgroundImage: getImageInput(style.layout.backgroundImage),
    },
  },
  theme: {
    colors: removeTypenames(style.theme.colors),
    logoLink: style.theme.logoLink,
    logo: getImageInput(style.theme.logo),
  },
});

const serializeFormBuilderOptionList = (
  list: Array<FormBuilder_OptionList>,
): Array<FormBuilder_OptionList__Input> => list.map(removeTypenames);

const serializeFormBuilder = (
  formBuilder: FormBuilderFragment,
): FormBuilder__Input => ({
  enabled: formBuilder.enabled,
  mode: formBuilder.mode,
  title: removeTypenames(formBuilder.title),
  subTitle: removeTypenames(formBuilder.subTitle),
  name: formBuilder.name,
  description: formBuilder.description,
  accountId: formBuilder.accountId,
  events: serializeFormBuilderEvents(formBuilder.events),
  nodes: serializeFormBuilderNodes(formBuilder.nodes),
  optionLists: serializeFormBuilderOptionList(formBuilder.optionLists),
  style: serializeFormBuilderStyle(formBuilder.style),
});

export default serializeFormBuilder;
